import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations, SUPPORTED_LANGUAGES } from "@/i18n/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "lopez.lang";

function detectInitialLang() {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch (_) {}
  const browser = (navigator?.language || "en").toLowerCase();
  return browser.startsWith("es") ? "es" : "en";
}

function resolvePath(obj, path) {
  return path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => detectInitialLang());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (_) {}
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const t = useCallback(
    (path, fallback = "") => {
      const value = resolvePath(translations[lang], path);
      if (value === undefined) {
        const enValue = resolvePath(translations.en, path);
        return enValue !== undefined ? enValue : fallback;
      }
      return value;
    },
    [lang]
  );

  const toggle = useCallback(() => {
    setLang((prev) => (prev === "en" ? "es" : "en"));
  }, []);

  const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, t, toggle]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
